import { useEffect, useRef } from 'react'
import DsButton from './Button'
import Eyebrow from './Eyebrow'
import { CheckIcon } from './icons'
import { SettingsSaveState } from './SettingsSaveContext'

export interface SettingsFooterTab {
  id: string,
  label: string,
  count?: number,
  danger?: boolean,
}

/**
 * Everything that lives below the content on a Settings screen.
 *
 * The save bar, when there is something to save, and the section menu, when it
 * has been opened. Both sit above the mobile tab bar; on desktop the menu is
 * gone (the tab row is on screen) and the bar returns to the bottom of the
 * window.
 *
 * The menu has no trigger of its own — the Settings tab in the bar below is the
 * trigger. A strip carrying one sat directly above that bar repeating the word
 * it already said, and cost 56px of a phone to do it.
 */
const SettingsFooter = ({ saveState, tabs, activeTabId, onSelectTab, isOpen, onClose }: {
  saveState: SettingsSaveState | null,
  tabs: SettingsFooterTab[],
  activeTabId: string,
  /** The caller decides whether the move is allowed — it is what knows there
   * are unsaved changes to warn about. */
  onSelectTab: (id: string) => void,
  isOpen: boolean,
  onClose: () => void,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Element | null;
      // The Settings tab is the trigger and toggles on its own; closing here
      // too would shut the menu and reopen it in the same gesture.
      if (target?.closest?.('[data-settings-menu-trigger]')) {
        return;
      }
      if (ref.current && !ref.current.contains(target as Node)) {
        onClose();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  const saveBar = saveState && saveState.dirtyCount > 0 ? (
    <div className="h-[62px] short:h-[52px] px-4 lg:px-8 bg-navy-800 border-t border-navy-500 flex items-center justify-between gap-3">
      <span className="min-w-0 truncate text-ds-135 text-navy-200">
        {saveState.dirtyCount} unsaved change{saveState.dirtyCount === 1 ? '' : 's'} in this section
      </span>
      <div className="flex items-center gap-2 flex-none">
        <DsButton variant="outline" onClick={saveState.onDiscard} isDisabled={saveState.isSubmitting}>
          Discard
        </DsButton>
        <DsButton
          variant="primary"
          onClick={saveState.onSave}
          isDisabled={saveState.isSubmitting || !saveState.canSave}
        >
          {saveState.isSubmitting ? 'Saving…' : 'Save changes'}
        </DsButton>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={ref}
      className="fixed z-30 left-0 right-0 lg:left-[232px] bottom-[calc(75px_+_env(safe-area-inset-bottom))] short:bottom-[calc(45px_+_env(safe-area-inset-bottom))] lg:bottom-0 flex flex-col"
    >
      {/* The panel, above both strips. It is a list rather than a native
          <select> so the counts and the danger tone survive. */}
      {isOpen ? (
        <div className="lg:hidden mx-3 mb-2 rounded-container border border-navy-500 bg-navy-800 shadow-[0_8px_32px_rgba(0,0,0,.5)] overflow-y-auto b4-thin max-h-[min(70vh,26rem,calc(100vh_-_120px))]">
          <div className="sticky top-0 z-10 bg-navy-800 px-4 pt-3.5 pb-2 short:pt-2 short:pb-1.5"><Eyebrow>settings</Eyebrow></div>
          {/* Sideways it goes two-up. A phone on its side is short and wide, and
              six rows in one column asked the short axis for everything while
              leaving half the wide one empty — the list scrolled for no reason.
              The 1px grid gaps over a navy ground draw the rules, so the same
              markup rules correctly in one column or two. */}
          <div className="flex flex-col short:grid short:grid-cols-2 gap-px bg-navy-700">
            {tabs.map(tab => {
              const isActive = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => { onClose(); onSelectTab(tab.id); }}
                  className={[
                    'b4-focus w-full min-h-[48px] short:min-h-[38px] px-4 flex items-center gap-3 text-left text-ds-135',
                    'transition-colors duration-[120ms]',
                    isActive
                      ? 'bg-navy-600 text-white font-semibold'
                      : `bg-navy-800 ${tab.danger ? 'text-state-err' : 'text-navy-100'}`,
                  ].join(' ')}
                >
                  <span className="flex-1 min-w-0 truncate">{tab.label}</span>
                  {tab.count ? (
                    <span className="flex-none font-courrier text-ds-115 text-navy-300">{tab.count}</span>
                  ) : null}
                  {isActive ? <CheckIcon size={16} className="flex-none" /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {saveBar}
    </div>
  );
}

export default SettingsFooter
